import React, { useState } from 'react';
import { PitchConfig, PitchMatch, ScheduleItem } from '../../data/pitchData';
import { FaCoffee, FaUtensils } from 'react-icons/fa';
import { GiBowlOfRice } from 'react-icons/gi';

interface PitchScheduleProps {
  pitch: PitchConfig;
}

function getBreakIcon(icon?: string) {
  switch (icon) {
    case 'coffee':
      return <FaCoffee />;
    case 'utensils':
      return <FaUtensils />;
    case 'bowl':
      return <GiBowlOfRice />;
    default:
      return null;
  }
}

function getSectionModifier(match: PitchMatch): string {
  if (match.section === 'final') return 'schedule-match--final';
  if (match.section === 'lower') return 'schedule-match--lower';
  return 'schedule-match--upper';
}

function ScheduleMatchRow({ match }: { match: PitchMatch }) {
  const modifier = getSectionModifier(match);

  return (
    <div className={`schedule-match ${modifier}`}>
      <span className="schedule-match__label">{match.label}</span>
      <span className="schedule-match__bo">{match.bo}</span>
      <span className="schedule-match__teams">
        {match.team1} vs {match.team2}
      </span>
      <span className="schedule-match__time">{match.time}</span>
      <span className="schedule-match__day">{match.day}</span>
    </div>
  );
}

function ScheduleBreakRow({ item }: { item: { label?: string; time?: string; icon?: string } }) {
  return (
    <div className="schedule-break">
      <span className="schedule-break__icon">{getBreakIcon(item.icon)}</span>
      <span className="schedule-break__label">{item.label}</span>
      <span className="schedule-break__time">{item.time}</span>
    </div>
  );
}

function ScheduleDividerRow({ label }: { label?: string }) {
  return (
    <div className="schedule-divider">
      <div className="schedule-divider__line" />
      <span className="schedule-divider__label">{label}</span>
      <div className="schedule-divider__line" />
    </div>
  );
}

function renderScheduleItems(items: ScheduleItem[], matchMap: Map<string, PitchMatch>) {
  return items.map((item, i) => {
    if (item.type === 'break') {
      return <ScheduleBreakRow key={i} item={item} />;
    }

    if (item.type === 'divider') {
      return <ScheduleDividerRow key={i} label={item.label} />;
    }

    if (item.type === 'match' && item.ids) {
      return (
        <div key={i} className="schedule-match-group">
          {item.ids.map((id: string) => {
            const match = matchMap.get(id);
            return match ? <ScheduleMatchRow key={id} match={match} /> : null;
          })}
        </div>
      );
    }

    return null;
  });
}

const PitchSchedule: React.FC<PitchScheduleProps> = ({ pitch }) => {
  const [groupFormat, setGroupFormat] = useState<'bo1' | 'bo3'>('bo1');
  const matchMap = new Map(pitch.matches.map((m) => [m.id, m]));

  const fridayItems = groupFormat === 'bo1' ? pitch.fridayBo1 : pitch.fridayBo3;

  const hasFriday = fridayItems.length > 0;

  return (
    <div className="pitch-schedule">
      {hasFriday && (
        <>
          <div className="schedule-day-label">FRIDAY</div>
          {pitch.hasGroupToggle && (
            <div className="group-format-toggle">
              <button
                className={`group-format-toggle__btn ${groupFormat === 'bo1' ? 'active' : ''}`}
                onClick={() => setGroupFormat('bo1')}
              >
                BO1
              </button>
              <button
                className={`group-format-toggle__btn ${groupFormat === 'bo3' ? 'active' : ''}`}
                onClick={() => setGroupFormat('bo3')}
              >
                BO3
              </button>
            </div>
          )}
        </>
      )}

      <div className="schedule-list">
        {hasFriday && renderScheduleItems(fridayItems, matchMap)}
        {renderScheduleItems(pitch.bracketSchedule, matchMap)}
      </div>
    </div>
  );
};

export default PitchSchedule;
